import { ExportCourseResponse } from '@api/lambdaApi';
import CheckBox from '@components/common/CheckBox';
import { Column, FlexColumn, Row, textOverflowStyle } from '@components/common/layoutUtils';
import TippyTooltip from '@components/common/tooltip/TippyTooltip';
import t from '@i18n/t';
import { LargeText, RegularText, SectionTitle, SmallText } from '@utils/typography';
import { useState } from 'react';
import styled from 'styled-components';
import { courseDetailsTableStyles } from './utils';
import { DialogActions } from '../common/ModalDialog';
import { courseActions } from '@state/course';
import Button from '@components/common/buttons/Button';
import ButtonWithLoader from '@components/common/buttons/ButtonWithLoader';
import { DATE_FORMAT, formatMomentDateAs, formatStringDateAs, isSameDate, toMomentDate } from 'shovel-lib/utils/timeUtils';
import { Formats, GradeWeightType } from 'shovel-lib/types';
import { secondsToDurationInMinutes } from '@utils/filters';
import { EmojiPreview } from '@components/common/emoji/EmojiPreview';
import { useDispatch, useSelector } from 'react-redux';
import { getImportCoursePreview } from '@state/calendar/selectors';
import { getType } from 'typesafe-actions';

type Props = {
  data: ExportCourseResponse;
  onClose: Function;
  onCancelImport?: () => void;
  onConfirmImport?: () => void;
  courseInfo?: { courseId: number; courseCorrelationId: string; courseName: string; courseColorHex: string };
};

function ImportCourseSyllabusPreview(props: Props) {
  const [includeSchedule, setIncludeSchedule] = useState<boolean>(true);
  const [includeTeachers, setIncludeTeachers] = useState<boolean>(true);
  const [includeGrading, setIncludeGrading] = useState<boolean>(true);

  const courseInfoFromState = useSelector(getImportCoursePreview);
  const { courseId, courseCorrelationId, courseName, courseColorHex } = props.courseInfo ?? courseInfoFromState;

  const dispatch = useDispatch();

  if (!courseId || !courseCorrelationId || !courseName || !courseColorHex) return null;

  const { data, onClose } = props;

  const course = data.info;

  return (
    <Column style={{ gap: 10 }}>
      <Row style={{ marginBottom: '20px', justifyContent: 'center' }}>
        <LargeText strong>You can adjust everything after import.</LargeText>
      </Row>

      <Row style={{ marginTop: '10px', marginBottom: '10px' }}>
        <TippyTooltip
          target={
            <div>
              <CheckBox
                checked={includeSchedule}
                onChange={() => setIncludeSchedule(!includeSchedule)}
                label={t.SCHEDULE}
                labelStyle={{ textTransform: 'uppercase' }}
              />
            </div>
          }
          content={t.INCLUDE_SCHEDULE_TOOLTIP}
        />
      </Row>
      <Column>{data.events.map((e, i) => renderCourseOccurrence(e, i))}</Column>

      <Row style={{ gap: 10, alignItems: 'flex-start' }}>
        <FlexColumn>
          {course.taskCategories?.length > 0 && (
            <Column>
              <SectionTitle strong>{t.TASK_CATEGORIES}</SectionTitle>
              <TaskCategoriesTable>
                <thead>
                  <tr>
                    <th>
                      <SmallText>{t.EMOJI}</SmallText>
                    </th>
                    <th>
                      <SmallText>{t.NAME}</SmallText>
                    </th>
                  </tr>
                </thead>
                <tbody>{course.taskCategories.map((tc, i) => renderTaskCategory(tc, i))}</tbody>
              </TaskCategoriesTable>
            </Column>
          )}
        </FlexColumn>
        <FlexColumn>
          {course.readingSources?.length > 0 && (
            <Column>
              <SectionTitle strong>{t.READING_SOURCES}</SectionTitle>
              <ReadingSourcesTable>
                <thead>
                  <tr>
                    <th>
                      <SmallText>{t.NAME}</SmallText>
                    </th>
                    <th>
                      <SmallText>{t.EST_TIME_PER_PAGE}</SmallText>
                    </th>
                  </tr>
                </thead>
                <tbody>{course.readingSources.map((rs, i) => renderReadingSource(rs, i))}</tbody>
              </ReadingSourcesTable>
            </Column>
          )}
        </FlexColumn>
      </Row>

      {course.tasks?.length > 0 && (
        <Column>
          <SectionTitle strong>{t.TASKS}</SectionTitle>
          <TasksTable>
            <thead>
              <tr>
                <th>
                  <SmallText>{t.NAME}</SmallText>
                </th>
                <th>
                  <SmallText>{t.TASK_CATEGORY}</SmallText>
                </th>
                <th>
                  <SmallText>{t.READING_SOURCE}</SmallText>
                </th>
                <th>
                  <SmallText>{t.DUE_DATE}</SmallText>
                </th>
              </tr>
            </thead>
            <tbody>{course.tasks.map((t, i) => renderTask(t, i, course.taskCategories, course.readingSources))}</tbody>
          </TasksTable>
        </Column>
      )}

      {course.gradeUnits?.length > 0 && (
        <Column>
          <TippyTooltip
            target={
              <div style={{ marginTop: 10, marginBottom: 10, width: 'fit-content' }}>
                <CheckBox
                  checked={includeGrading}
                  onChange={() => setIncludeGrading(!includeGrading)}
                  label={t.GRADING_RUBRIC}
                  labelStyle={{ textTransform: 'uppercase' }}
                />
              </div>
            }
            content={t.INCLUDE_GRADING_TOOLTIP}
          />
          <Column>
            {course.gradeUnits.map((gu, i) =>
              renderGradeUnit(gu, i, course.weightType === GradeWeightType.PERCENTAGES ? '%' : 'Pts')
            )}
          </Column>
        </Column>
      )}

      {course.teachers?.length > 0 && (
        <Column style={{ marginTop: 10 }}>
          <TippyTooltip
            target={
              <div style={{ marginBottom: 10, width: 'fit-content' }}>
                <CheckBox
                  checked={includeTeachers}
                  onChange={() => setIncludeTeachers(!includeTeachers)}
                  label={t.TEACHERS}
                  labelStyle={{ textTransform: 'uppercase' }}
                />
              </div>
            }
            content={t.INCLUDE_TEACHERS_TOOLTIP}
          />
          <TeachersTable>
            <thead>
              <tr>
                <th>
                  <SmallText>{t.NAME}</SmallText>
                </th>
                <th>
                  <SmallText>{t.EMAIL}</SmallText>
                </th>
                <th>
                  <SmallText>{t.DETAILS}</SmallText>
                </th>
              </tr>
            </thead>
            <tbody>{course.teachers.map((t, i) => renderTeacher(t, i))}</tbody>
          </TeachersTable>
        </Column>
      )}
      {course.notes && (
        <Column style={{ marginBottom: 20 }}>
          <SectionTitle strong>{t.NOTES}</SectionTitle>
          <div>
            <RegularText>{course.notes}</RegularText>
          </div>
        </Column>
      )}

      <DialogActions position={'center'}>
        <Button
          onClick={() => {
            props.onCancelImport?.();
            onClose();
          }}
          withoutBorder
        >
          {t.CANCEL} {t.IMPORT}
        </Button>
        <ButtonWithLoader
          onClick={() =>
            dispatch({
              type: getType(courseActions.importCourse.request),
              payload: {
                courseId,
                courseCorrelationId,
                courseName,
                courseColorHex,
                includeSchedule,
                includeTeachers,
                includeGrading,
                course: data
              }
            })
          }
          action={courseActions.importCourse}
          callback={() => {
            props.onConfirmImport?.();
            onClose();
          }}
          text={`${t.CONFIRM} ${t.IMPORT}`}
          filled
        />
      </DialogActions>
    </Column>
  );
}

export default ImportCourseSyllabusPreview;

function renderTaskCategory(taskCategory: { name: string; emoji: string }, index: number) {
  return (
    <tr key={index}>
      <td>
        <EmojiPreview emoji={taskCategory.emoji} />
      </td>
      <td>
        <RegularText>{taskCategory.name}</RegularText>
      </td>
    </tr>
  );
}

function renderReadingSource(readingSource: { name: string; secondsPerPage: number }, index: number) {
  return (
    <tr key={index}>
      <td>
        <RegularText>{readingSource.name}</RegularText>
      </td>
      <td>
        <RegularText>{secondsToDurationInMinutes(readingSource.secondsPerPage)}</RegularText>
      </td>
    </tr>
  );
}

function renderTask(task: any, index: number, taskCategories: any[], readingSources: any[]) {
  const taskCategory = task.taskCategoryId ? taskCategories.find(tc => task.taskCategoryId === tc.id) : null;
  const readingSource = task.readingSourceId ? readingSources.find(rs => task.readingSourceId === rs.id) : null;
  return (
    <tr key={index}>
      <td>
        <RegularText>{task.title}</RegularText>
      </td>
      <td>
        <RegularText>{taskCategory ? `${taskCategory.emoji} ${taskCategory.name}` : '-'}</RegularText>
      </td>
      <td>
        <RegularText>{readingSource ? readingSource.name : '-'}</RegularText>
      </td>
      <td>
        <RegularText>{task.dueDate ? formatStringDateAs(task.dueDate, Formats.DATE_TIME_FORMAT) : '-'}</RegularText>
      </td>
    </tr>
  );
}

function renderGradeUnit(gradeUnit: any, index: number, weightTypeLabel: string) {
  return (
    <Column key={index} style={{ marginBottom: '10px' }}>
      <GradeUnitTitle>
        <Item width={200}>{gradeUnit.name}</Item>
        <Item width={200}>
          {gradeUnit.weight} {weightTypeLabel}
        </Item>
      </GradeUnitTitle>
      {gradeUnit.grades?.length > 0 && (
        <GradesTable>
          <thead>
            <tr>
              <th>{/* <SmallText>{t.NAME}</SmallText> */}</th>
              <th>{/* <SmallText>{t.DATE}</SmallText> */}</th>
            </tr>
          </thead>
          <tbody>
            {gradeUnit.grades.map((grade, i) => (
              <tr key={i}>
                <td>
                  <RegularText>{grade.date ? formatStringDateAs(grade.date, DATE_FORMAT) : '-'}</RegularText>
                </td>
                <td>
                  <RegularText>{grade.name || '-'}</RegularText>
                </td>
              </tr>
            ))}
          </tbody>
        </GradesTable>
      )}
    </Column>
  );
}

function renderTeacher(teacher: { fullName: string; email: string; details: string }, index: number) {
  return (
    <tr key={index}>
      <td>
        <RegularText>{teacher.fullName}</RegularText>
      </td>
      <td>
        <RegularText>{teacher.email}</RegularText>
      </td>
      <td>
        <RegularText>{teacher.details}</RegularText>
      </td>
    </tr>
  );
}

function renderCourseOccurrence(event: any, index: number) {
  return <Row key={index}>{courseOccurrenceText(event)}</Row>;
}

function courseOccurrenceText(event: any) {
  const start = toMomentDate(event.start);
  const end = toMomentDate(event.start).add(event.durationInMinutes, 'minutes');
  if (!event.isRecurring) {
    return (
      <RegularText style={{ marginBottom: '4px' }}>
        {isSameDate(start, end)
          ? `${formatMomentDateAs(start, Formats.MONTH_DAY_FORMAT)}, ${formatMomentDateAs(
              start,
              Formats.TIME
            )} - ${formatMomentDateAs(end, Formats.TIME)}`
          : `${formatMomentDateAs(start, Formats.DATE_TIME_FORMAT)} - ${formatMomentDateAs(end, Formats.DATE_TIME_FORMAT)}`}
      </RegularText>
    );
  }

  if (event.recurrenceText?.includes(' until')) {
    const recurrence = event.recurrenceText.split(' until')[0];
    return (
      <RegularText style={{ marginBottom: '4px' }}>
        {formatMomentDateAs(start, Formats.TIME)} - {formatMomentDateAs(end, Formats.TIME)} {recurrence}
      </RegularText>
    );
  }

  return <RegularText style={{ marginBottom: '4px' }}>{event.recurrenceText}</RegularText>;
}

const TaskCategoriesTable = styled.table`
  ${courseDetailsTableStyles};

  th:nth-child(1),
  td:nth-child(1) {
    width: 40px;
    // text-align: center;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: calc(100%-40px);
    border-right: 0;
  }

  th:last-child {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }

  td:last-child {
    border: 1px solid ${props => props.theme.borderColor};
    border-right: 0;
  }
`;

const ReadingSourcesTable = styled.table`
  ${courseDetailsTableStyles};

  th:nth-child(1),
  td:nth-child(1) {
    width: calc(100%-110px);
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 110px;
    border-right: 0;
  }

  th:last-child {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }

  td:last-child {
    border: 1px solid ${props => props.theme.borderColor};
    border-right: 0;
  }
`;

const TeachersTable = styled.table`
  ${courseDetailsTableStyles};

  th:nth-child(1),
  td:nth-child(1),
  th:nth-child(2),
  td:nth-child(2) {
    width: 30%;
  }

  th:nth-child(3),
  td:nth-child(3) {
    width: 40%;
  }

  th:last-child {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }

  td:last-child {
    border: 1px solid ${props => props.theme.borderColor};
    border-right: 0;
  }
`;

const TasksTable = styled.table`
  ${courseDetailsTableStyles};

  th:nth-child(1),
  td:nth-child(1),
  th:nth-child(2),
  td:nth-child(2),
  th:nth-child(3),
  td:nth-child(3) {
    width: 27%;
  }

  th:nth-child(4),
  td:nth-child(4) {
    width: 19%;
  }

  th:last-child {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }

  td:last-child {
    border: 1px solid ${props => props.theme.borderColor};
    border-right: 0;
  }
`;

const GradesTable = styled.table`
  ${courseDetailsTableStyles};

  width: 300px;

  th:nth-child(1),
  td:nth-child(1) {
    width: 100px;
  }

  th:nth-child(2),
  td:nth-child(2) {
    width: 200px;
    border-right: 0;
  }

  th:last-child {
    border-bottom: 1px solid ${props => props.theme.borderColor};
  }

  td:last-child {
    border: 1px solid ${props => props.theme.borderColor};
    border-right: 0;
  }
`;

const Item = styled(RegularText)<{ width?: number }>`
  ${props => props.width && `width: ${props.width}px;`}
  ${textOverflowStyle}
`;

const GradeUnitTitle = styled(Row)`
  margin-bottom: 4px;
`;
